import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  LinearProgress,
  useTheme,
  alpha,
  Tooltip,
  Divider,
  Button,
  IconButton,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { SurfaceCard } from '../ui/SurfaceCard';
import { StatusPill, StatusTone } from '../ui/StatusPill';
import { tokens } from '../../theme';
import { formatDateTime, formatPairPrice, formatTokenAmount, formatUsdOrDash } from '../../utils/formatters';
import { IBuyBack } from '../../shared/types';
import { ReconnectingWebSocket } from '../../socket';
import OrderSizeConfigModal from '../modals/OrderSizeConfigModal';

type BotType = 'dipster' | 'dumper';

interface BotStatusCardProps {
  type: BotType;
  pendingTasks: IBuyBack[];
  onPauseAll?: () => void;
  onResumeAll?: () => void;
  onAddTask?: () => void;
  disabled?: boolean;
  socket?: ReconnectingWebSocket;
  onConfigSuccess?: (message: string) => void;
}

/** USD amounts (spent, budget): the shared card rule (formatUsd); zero reads "—". */
const usd = formatUsdOrDash;

/** Not applicable / unknown: "—" in the disabled text colour. */
const Dash: React.FC = () => (
  <Box component="span" sx={{ color: 'text.disabled' }}>
    —
  </Box>
);

/** Unit after an amount: small muted suffix ("18,421 USDT"), as in the Buybacks stats. */
const unitSx = { fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', letterSpacing: 0 } as const;
const Unit: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ ...unitSx, ml: 0.5 }}>
    {children}
  </Box>
);

/** USD figure, or a disabled "—" for zero / unknown. */
const UsdValue: React.FC<{ value: number | null | undefined }> = ({ value }) => (value ? <>{usd(value)}</> : <Dash />);

/** Token amount with its unit ("412,300 RIF"), or a disabled "—" for zero / unknown. */
const TokenValue: React.FC<{ value: number | null | undefined; unit: string }> = ({ value, unit }) =>
  value ? (
    <>
      {formatTokenAmount(value)}
      <Unit>{unit}</Unit>
    </>
  ) : (
    <Dash />
  );

/**
 * TokenValue for a tile figure: where three tiles share a half-width card (iPad landscape) the unit
 * drops under the amount, flush left like the tiles' captions, instead of cutting the amount short.
 */
const TokenFigure: React.FC<{ value: number | null | undefined; unit: string }> = ({ value, unit }) =>
  value ? (
    <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0.5, minWidth: 0 }}>
      <Box component="span" sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {formatTokenAmount(value)}
      </Box>
      <Box component="span" sx={unitSx}>
        {unit}
      </Box>
    </Box>
  ) : (
    <Dash />
  );

/** Statuses of tasks that are over. */
const CLOSED_STATUSES = new Set(['COMPLETED', 'FAILED', 'CANCELLED']);

/** One side of a card's open tasks: buys in USDT (spent of budget), sells in RIF (sold of target). */
interface SideProgress {
  count: number;
  done: number;
  target: number;
}

/** RIF prices keep 4 decimals ($0.0923); prices from $1 up show cents. Pair formatter, as in the task list. */
const formatPrice = (price: number, task?: IBuyBack) =>
  formatPairPrice(price, task?.baseAsset || 'RIF', task?.quoteAsset || 'USDT', price < 1 ? 4 : 2);

/** Age of a timestamp: "just now", "12m ago", "5h ago", "9d ago". */
const timeAgo = (ts: number | null | undefined): string | null => {
  if (!ts || !Number.isFinite(ts)) return null;
  const minutes = Math.max(0, Math.floor((Date.now() - ts) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`;
};

/**
 * Second line of a pending order: the prices and rate the task really carries. A sell's limit is its
 * "Min price", a buy's its "Max price" (never "Floor": the floor tile above is the bot's). A task with
 * none of them (a queued buy) shows its limit as not set, so every row keeps two lines.
 */
const pendingDetails = (task: IBuyBack): { label: string; value: string | null }[] => {
  const isSell = task.taskType === 'RIF_SELL';
  const details: { label: string; value: string | null }[] = [];
  if (task.activationPrice != null) details.push({ label: 'Activation', value: formatPrice(task.activationPrice, task) });
  if (task.minPrice != null) details.push({ label: isSell ? 'Min price' : 'Max price', value: formatPrice(task.minPrice, task) });
  if (task.adaptiveBuyRate != null && task.adaptiveBuyRate > 0) {
    details.push({ label: 'Rate', value: `$${task.adaptiveBuyRate.toFixed(0)}/min` });
  }
  if (details.length === 0) details.push({ label: isSell ? 'Min price' : 'Max price', value: null });
  return details;
};

const BotStatusCard: React.FC<BotStatusCardProps> = ({
  type,
  pendingTasks,
  onPauseAll,
  onResumeAll,
  onAddTask,
  disabled = false,
  socket,
  onConfigSuccess,
}) => {
  const theme = useTheme();
  const [configModalOpen, setConfigModalOpen] = useState(false);

  // Filter tasks by label (dipster/dumper) — both bots buy AND sell. Only open tasks: failed and
  // cancelled ones would inflate the budgets and the "x of N" count.
  // Fallback: DUMPER RIF_SELL tasks may have been created with wrong label,
  // so also match by botId for robustness.
  const botTasks = React.useMemo(() => {
    return pendingTasks.filter(t => {
      if (t.taskType !== 'RIF_BUY' && t.taskType !== 'RIF_SELL') return false;
      if (CLOSED_STATUSES.has(String(t.status ?? '').toUpperCase())) return false;
      if (t.label === type) return true;
      // Fallback: DUMPER botId always belongs to the dumper card
      if (type === 'dumper' && t.botId === 'DUMPER') return true;
      return false;
    });
  }, [pendingTasks, type]);

  const stats = React.useMemo(() => {
    const activeTasks = botTasks.filter(t => t.status === 'ACTIVE');
    const pausedTasks = botTasks.filter(t => t.status === 'PAUSED');
    const pendingCount = botTasks.filter(t => t.status === 'PENDING').length;
    const executingTasks = activeTasks.filter(t => t.activeOrderId != null);

    // Most recent USDT balance (quote)
    const taskWithBalance = [...botTasks]
      .filter(t => t.accountQuoteBalance != null && t.accountQuoteBalance > 0)
      .sort((a, b) => (b.lastUpdatedAt || 0) - (a.lastUpdatedAt || 0))[0];
    const totalBalance = taskWithBalance?.accountQuoteBalance || 0;

    // Most recent RIF balance (base) — written by sell cycles
    const taskWithBaseBalance = [...botTasks]
      .filter(t => t.accountBaseBalance != null && (t.accountBaseBalance as number) > 0)
      .sort((a, b) => (b.lastUpdatedAt || 0) - (a.lastUpdatedAt || 0))[0];
    const baseBalance = (taskWithBaseBalance?.accountBaseBalance as number) || 0;

    // Progress, per side: buy targets are quote budgets (USDT), sell targets base amounts (RIF), so each
    // side sums its own tasks and the two are never added together.
    const isSellBot = type === 'dumper';
    const buyTasks = botTasks.filter(t => t.taskType !== 'RIF_SELL');
    const sellTasks = botTasks.filter(t => t.taskType === 'RIF_SELL');
    const total = (tasks: IBuyBack[], pick: (t: IBuyBack) => number | undefined) =>
      tasks.reduce((sum, t) => sum + (pick(t) || 0), 0);
    const buys: SideProgress = {
      count: buyTasks.length,
      done: total(buyTasks, t => t.totalQuoteSpent), // USDT spent
      target: total(buyTasks, t => t.targetAmount), // USDT budget
    };
    const sells: SideProgress = {
      count: sellTasks.length,
      done: total(sellTasks, t => t.totalBaseBought), // RIF sold
      target: total(sellTasks, t => t.targetAmount), // RIF to sell
    };
    const sellUnit = sellTasks[0]?.baseAsset || 'RIF';

    // PENDING orders on this bot's side: sells (activation + floor price) for the dumper, buys for the dipster
    const pendingOrders = botTasks.filter(
      t => t.status === 'PENDING' && t.taskType === (isSellBot ? 'RIF_SELL' : 'RIF_BUY')
    );

    // Bot floor: the lowest limit among the open sell tasks (what the tile's tooltip promises).
    const floors = sellTasks
      .map(t => t.minPrice)
      .filter((p): p is number => p != null && Number.isFinite(p) && p > 0);
    const floorPrice = floors.length > 0 ? Math.min(...floors) : null;

    // Overall status
    let status: 'active' | 'paused' | 'idle' | 'executing' | 'pending' = 'idle';
    if (executingTasks.length > 0) status = 'executing';
    else if (activeTasks.length > 0) status = 'active';
    else if (pausedTasks.length > 0) status = 'paused';
    else if (pendingCount > 0) status = 'pending';

    // Adaptive metrics (across all tasks with data)
    const tasksWithAdaptive = botTasks.filter(t => t.adaptiveBuyRate != null && t.adaptiveBuyRate > 0);
    const avgAdaptiveBuyRate = tasksWithAdaptive.length > 0
      ? tasksWithAdaptive.reduce((sum, t) => sum + (t.adaptiveBuyRate || 0), 0) / tasksWithAdaptive.length
      : null;
    const avgMarketBuyRatio = tasksWithAdaptive.length > 0
      ? tasksWithAdaptive.reduce((sum, t) => sum + (t.marketBuyRatio || 0), 0) / tasksWithAdaptive.length
      : null;

    return {
      totalTasks: botTasks.length,
      activeTasks: activeTasks.length,
      pausedTasks: pausedTasks.length,
      pendingTasks: pendingCount,
      executingTasks: executingTasks.length,
      pendingOrders,
      totalBalance,
      baseBalance,
      buys,
      sells,
      sellUnit,
      status,
      hasActiveOrders: executingTasks.length > 0,
      activeOrderPrice: executingTasks[0]?.activeOrderPrice || null,
      activeOrderTask: executingTasks[0] as IBuyBack | undefined,
      avgAdaptiveBuyRate,
      avgMarketBuyRatio,
      floorPrice,
    };
  }, [botTasks, type]);

  // Identity tints (not status colors): Dipster blue, Dumper rose.
  const accentColor = type === 'dipster' ? tokens.chart.green : tokens.chart.violet;
  const title = type === 'dipster' ? 'Dipster Bot' : 'Dumper Bot';
  const subtitle = type === 'dipster' ? 'trading@tekscapital.com' : 'operations@tekscapital.com';

  // In progress = gold; queued = neutral with a hollow dot (same as the "Pending" pills of the rows
  // below; blue is the Dipster's identity, never a status); idle/paused = neutral.
  const statusConfig: { label: string; tone: StatusTone; pulse: boolean; hollow?: boolean } = {
    executing: { label: 'Executing', tone: 'brand' as const, pulse: true },
    active:    { label: 'Active',    tone: 'brand' as const, pulse: false },
    paused:    { label: 'Paused',    tone: 'neutral' as const, pulse: false },
    idle:      { label: 'No tasks',  tone: 'neutral' as const, pulse: false },
    pending:   { label: 'Pending',   tone: 'neutral' as const, pulse: false, hollow: true },
  }[stats.status];

  const divider = <Divider sx={{ borderColor: tokens.borderSubtle }} />;
  const insetBox = {
    flex: 1,
    minWidth: 0,
    // Three tiles share a half-width card from md up: tighter padding until xl.
    p: { xs: 1.5, sm: 2, md: 1.5, xl: 2 },
    bgcolor: tokens.glass.inset,
    border: `1px solid ${tokens.borderSubtle}`,
    borderRadius: '16px',
  };
  const label = { fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary' };
  const figure = {
    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.25rem', xl: '1.5rem' },
    fontWeight: 650,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
  };
  // Tile icons drop out where three tiles share a half-width card, so the labels stay on one line.
  const tileIcon = { fontSize: 15, display: { xs: 'inline-flex', md: 'none', xl: 'inline-flex' } };

  const tiles: {
    key: string;
    icon?: React.ReactNode;
    label: string;
    value: React.ReactNode;
    /** Secondary line under the figure. */
    caption?: string;
    tooltip?: string;
  }[] = [
    {
      key: 'balance',
      icon: <AccountBalanceWalletRoundedIcon sx={tileIcon} />,
      // Token amounts, not dollars: the dipster buys RIF with the USDT it holds, the dumper sells its RIF.
      label: 'Balance',
      value:
        type === 'dumper' ? (
          <TokenFigure value={stats.baseBalance} unit="RIF" />
        ) : (
          <TokenFigure value={stats.totalBalance} unit="USDT" />
        ),
    },
    {
      key: 'tasks',
      icon: <AssignmentRoundedIcon sx={tileIcon} />,
      label: 'Active tasks',
      value: (
        <>
          {stats.activeTasks}
          <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.secondary', ml: 0.5 }}>
            of {stats.totalTasks}
          </Box>
        </>
      ),
      caption: `${stats.pendingTasks} pending${stats.pausedTasks > 0 ? `, ${stats.pausedTasks} paused` : ''}`,
    },
    // Always a third tile, so both cards keep the same three-tile row and their sections line up.
    type === 'dumper'
      ? {
          key: 'floor',
          icon: <ShieldOutlinedIcon sx={tileIcon} />,
          // The bot's floor; a task's own limit reads "Min price" in its pending row, never "Floor".
          label: 'Bot floor',
          value: stats.floorPrice != null ? formatPrice(stats.floorPrice) : <Dash />,
          tooltip: stats.floorPrice != null ? 'Lowest price the open sell tasks will sell at' : undefined,
        }
      : {
          key: 'rate',
          icon: <SpeedRoundedIcon sx={tileIcon} />,
          label: 'Rate',
          value: stats.avgAdaptiveBuyRate != null ? `$${stats.avgAdaptiveBuyRate.toFixed(0)}/min` : <Dash />,
          tooltip:
            stats.avgAdaptiveBuyRate == null
              ? undefined
              : stats.avgMarketBuyRatio != null
              ? `Market buy ratio ${(stats.avgMarketBuyRatio * 100).toFixed(1)}% — ${
                  stats.avgMarketBuyRatio > 0.6
                    ? 'strong buy pressure'
                    : stats.avgMarketBuyRatio > 0.4
                    ? 'neutral'
                    : 'weak, bot compensating'
                }`
              : 'Adaptive trade rate',
        },
  ];

  // Which progress bars the card shows: the sides its open tasks have, or its own side when it has none.
  const hasNoTasks = stats.buys.count === 0 && stats.sells.count === 0;
  const progressSides = (['buy', 'sell'] as const).filter(side =>
    side === 'buy'
      ? stats.buys.count > 0 || (hasNoTasks && type === 'dipster')
      : stats.sells.count > 0 || (hasNoTasks && type === 'dumper')
  );

  // Phones: the third metric as a row (label left, value right) under a hairline; tiles elsewhere.
  const lastTile = tiles[tiles.length - 1];
  const metricRow = (
    <Box
      sx={{
        display: { xs: 'flex', sm: 'none' },
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        minHeight: 44,
        mt: 1.5,
        borderTop: `1px solid ${tokens.borderSubtle}`,
        cursor: lastTile.tooltip ? 'help' : undefined,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} useFlexGap sx={{ color: 'text.secondary', minWidth: 0 }}>
        {lastTile.icon}
        <Typography sx={label} noWrap>
          {lastTile.label}
        </Typography>
      </Stack>
      <Typography component="div" noWrap sx={{ fontSize: '0.9375rem', fontWeight: 650, letterSpacing: '-0.01em', flexShrink: 0 }}>
        {lastTile.value}
      </Typography>
    </Box>
  );
  const phoneMetricRow = lastTile.tooltip ? (
    <Tooltip title={lastTile.tooltip} arrow>
      {metricRow}
    </Tooltip>
  ) : (
    metricRow
  );

  return (
    <SurfaceCard padding="none" tint={accentColor} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header: the icon, then the name over the account email (the pair centred on the icon);
          status and settings on the right. On narrow cards (phones, narrow windows) the status pill
          sits on the name's line, still on the right, so the email can run under it in full. */}
      <Box sx={{ px: { xs: 2.5, md: 3 }, pt: { xs: 2.5, md: 3 }, pb: 2, containerType: 'inline-size' }}>
        <Box
          sx={{
            display: 'grid',
            alignItems: 'center',
            columnGap: 1,
            gridTemplateColumns: '44px minmax(0, 1fr) auto auto',
            gridTemplateAreas: `"icon title pill settings" "icon email pill settings"`,
            // Narrow: the pill moves up to the name's line and the email spans under it. Slightly tighter
            // gaps and a 32px settings button (44px touch area from the theme) keep "Dumper Bot" whole
            // next to "Executing" down to 360px phones.
            '@container (max-width: 369.98px)': {
              columnGap: 0.75,
              gridTemplateAreas: `"icon title pill settings" "icon email email settings"`,
              '& > .bot-status': { alignSelf: 'end' },
              '& > .bot-icon': { mr: 0.75 },
              '& > .bot-settings': { width: 32, height: 32 },
            },
          }}
        >
          <Box
            className="bot-icon"
            sx={{
              gridArea: 'icon',
              mr: 0.5,
              width: 44,
              height: 44,
              borderRadius: '14px',
              bgcolor: alpha(accentColor, 0.16),
              border: `1px solid ${alpha(accentColor, 0.32)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: accentColor,
            }}
          >
            <SmartToyRoundedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography
            sx={{ gridArea: 'title', alignSelf: 'end', fontSize: '1.0625rem', fontWeight: 650, letterSpacing: '-0.01em', lineHeight: 1.3 }}
            noWrap
          >
            {title}
          </Typography>
          <Typography
            title={subtitle}
            sx={{ gridArea: 'email', alignSelf: 'start', mt: 0.25, fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.35, color: 'text.secondary' }}
            noWrap
          >
            {subtitle}
          </Typography>
          <Box className="bot-status" sx={{ gridArea: 'pill', display: 'flex', justifyContent: 'flex-end' }}>
            <StatusPill
              tone={statusConfig.tone}
              label={statusConfig.label}
              pulsing={statusConfig.pulse}
              hollowDot={statusConfig.hollow}
              size="sm"
            />
          </Box>
          {socket && (
            <Tooltip title="Configure order size & cycle" arrow>
              <IconButton
                className="bot-settings"
                onClick={() => setConfigModalOpen(true)}
                aria-label="Configure order size and cycle"
                sx={{
                  gridArea: 'settings',
                  width: 36,
                  height: 36,
                  border: `1px solid ${tokens.border}`,
                  color: 'text.secondary',
                  bgcolor: 'rgba(255,255,255,0.04)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: 'text.primary' },
                }}
              >
                <TuneRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Metrics: three tiles (balance · tasks · rate or floor) in one row whenever the card is wide enough
          for their figures (tablets, desktop: both cards switch together, so their sections line up);
          2 + 1 on narrow two-column windows. Phones: the two tiles, then the third metric (one short
          value) as a 44px row under a hairline instead of a full-width tile. */}
      <Box sx={{ px: { xs: 2.5, md: 3 }, pb: { xs: 1, sm: 2.5, md: 3 }, containerType: 'inline-size' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 1.25,
            '@container (min-width: 340px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
            // After the container rule, so phones keep two columns whatever the card width.
            [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
          }}
        >
          {tiles.map((t, i) => {
            const isLast = i === tiles.length - 1;
            const tile = (
              <Box
                key={t.key}
                sx={{
                  ...insetBox,
                  display: isLast ? { xs: 'none', sm: 'block' } : undefined,
                  gridColumn: isLast ? '1 / -1' : undefined,
                  '@container (min-width: 340px)': { gridColumn: 'auto' },
                  cursor: t.tooltip ? 'help' : undefined,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.75} useFlexGap sx={{ color: 'text.secondary', mb: 0.5 }}>
                  {t.icon}
                  <Typography sx={label}>{t.label}</Typography>
                </Stack>
                <Typography component="div" sx={figure} noWrap>
                  {t.value}
                </Typography>
                {t.caption && (
                  <Typography sx={{ mt: 0.25, fontSize: '0.75rem', color: 'text.secondary' }} noWrap>
                    {t.caption}
                  </Typography>
                )}
              </Box>
            );
            return t.tooltip ? (
              <Tooltip key={t.key} title={t.tooltip} arrow>
                {tile}
              </Tooltip>
            ) : (
              tile
            );
          })}
        </Box>
        {phoneMetricRow}
      </Box>

      {/* Pending orders: buys (Dipster) / sells (Dumper). Both cards always show the section, and it
          takes the spare height (the live-order note included), so the header and tiles above and the
          progress and actions below line up across both cards. */}
      {divider}
      <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2, flex: '1 1 auto' }}>
        {stats.hasActiveOrders && stats.activeOrderPrice && (
          // Orders on the book = in progress: gold.
          <Box
            sx={{
              mb: 1.5,
              px: 1.5,
              py: 1.25,
              bgcolor: alpha(theme.palette.secondary.main, 0.08),
              borderRadius: '14px',
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.24)}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <SyncRoundedIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'secondary.main' }}>
                {stats.executingTasks} active order{stats.executingTasks > 1 ? 's' : ''} @ {formatPrice(stats.activeOrderPrice, stats.activeOrderTask)}
              </Typography>
            </Stack>
          </Box>
        )}
        <Typography sx={{ ...label, fontWeight: 600, display: 'block', mb: 1 }}>
          {type === 'dumper' ? 'Pending sell orders' : 'Pending buy orders'}
        </Typography>
        {stats.pendingOrders.length > 0 ? (
          <Stack spacing={1}>
            {stats.pendingOrders.map(task => {
              const isSell = task.taskType === 'RIF_SELL';
              // Sells are sized in the base token (RIF), buys in their quote budget (USDT).
              const done = isSell ? task.totalBaseBought : task.totalQuoteSpent;
              const started = done > 0 && task.targetAmount > 0;
              // The pill already says it has not started: the right side says since when instead.
              const queuedAt = task.createdAt ?? task.lastUpdatedAt;
              const age = timeAgo(queuedAt);
              // Every row has the same two lines (status + size, then its prices), so a Dipster row and a
              // Dumper row are the same height and the two cards line up.
              const details = pendingDetails(task);
              return (
                <Box
                  key={task.uuid}
                  sx={{
                    p: 1.5,
                    bgcolor: tokens.glass.inset,
                    border: `1px solid ${alpha(accentColor, 0.22)}`,
                    borderRadius: '14px',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <StatusPill tone="neutral" hollowDot label="Pending" size="sm" />
                      <Typography variant="caption" fontWeight={600} color="text.primary" noWrap>
                        {isSell ? (
                          <TokenValue value={task.targetAmount} unit={task.baseAsset || 'RIF'} />
                        ) : (
                          <UsdValue value={task.targetAmount} />
                        )}
                      </Typography>
                    </Stack>
                    {(started || age) && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        title={started ? undefined : formatDateTime(queuedAt)}
                        sx={{ flexShrink: 0 }}
                      >
                        {started
                          ? `${((done / task.targetAmount) * 100).toFixed(1)}% ${isSell ? 'sold' : 'spent'}`
                          : `Queued ${age}`}
                      </Typography>
                    )}
                  </Stack>
                  <Typography
                    component="div"
                    noWrap
                    sx={{ mt: 1, fontSize: '0.75rem', lineHeight: 1.5, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {details.map((d, i) => (
                      <React.Fragment key={d.label}>
                        {i > 0 && ' · '}
                        {d.label}{' '}
                        <Box component="span" sx={{ color: 'text.primary', fontWeight: 650 }}>
                          {d.value ?? <Dash />}
                        </Box>
                      </React.Fragment>
                    ))}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
            {type === 'dumper' ? 'No pending sell orders' : 'No pending buy orders'}
          </Typography>
        )}
      </Box>

      {divider}

      {/* Progress: buys (spent of budget, USD) and sells (sold of target, RIF) each on their own bar; a
          card shows the side its open tasks have (both when it has both, its own side when it has none). */}
      <Stack spacing={2.5} sx={{ px: { xs: 2.5, md: 3 }, py: { xs: 2.5, md: 3 } }}>
        {progressSides.map(side => {
          const p = side === 'sell' ? stats.sells : stats.buys;
          const pct = p.target > 0 ? (p.done / p.target) * 100 : 0;
          const figure = (value: number) =>
            side === 'sell' ? <TokenValue value={value} unit={stats.sellUnit} /> : <UsdValue value={value} />;
          return (
            <Box key={side}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={1.25}>
                <Box>
                  <Typography sx={label}>{side === 'sell' ? 'Sold' : 'Spent'}</Typography>
                  <Typography component="div" sx={{ fontSize: '1.125rem', fontWeight: 650 }}>
                    {figure(p.done)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={label}>{side === 'sell' ? 'Target' : 'Budget'}</Typography>
                  <Typography component="div" sx={{ fontSize: '1.125rem', fontWeight: 650 }}>
                    {figure(p.target)}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1.25}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(pct, 100)}
                  aria-label={`${side === 'sell' ? 'Sold' : 'Spent'}: ${pct.toFixed(1)}% of the ${side === 'sell' ? 'target' : 'budget'}`}
                  sx={{
                    flex: 1,
                    height: 8,
                    borderRadius: 999,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      background: tokens.goldGradient,
                    },
                  }}
                />
                <Typography
                  component="div"
                  sx={{ fontSize: '0.8125rem', fontWeight: 650, fontVariantNumeric: 'tabular-nums', minWidth: 44, textAlign: 'right' }}
                >
                  {pct > 0 ? `${pct.toFixed(1)}%` : <Dash />}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Stack>

      {divider}

      {/* Actions */}
      <Box sx={{ px: { xs: 2.5, md: 3 }, py: { xs: 2, md: 2.5 }, mt: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: onAddTask ? '1.2fr 1fr 1fr' : '1fr 1fr' }, gap: 1 }}>
          {onAddTask && (
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={onAddTask}
              disabled={disabled}
              sx={{
                gridColumn: { xs: '1 / -1', sm: 'auto' },
                whiteSpace: 'nowrap',
                // In-card action: no glow. Matches the theme's `.MuiButton-contained.MuiButton-colorPrimary` specificity.
                '&.MuiButton-contained, &.MuiButton-contained:hover': { boxShadow: 'none' },
              }}
            >
              Add task
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<PauseRoundedIcon />}
            onClick={onPauseAll}
            disabled={disabled || stats.activeTasks === 0}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Pause all
          </Button>
          <Button
            variant="outlined"
            startIcon={<PlayArrowRoundedIcon />}
            onClick={onResumeAll}
            disabled={disabled || stats.pausedTasks === 0}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Resume all
          </Button>
        </Box>
      </Box>

      {/* Order Size Config Modal */}
      {socket && (
        <OrderSizeConfigModal
          open={configModalOpen}
          onClose={() => setConfigModalOpen(false)}
          botType={type}
          socket={socket}
          onSuccess={onConfigSuccess}
        />
      )}
    </SurfaceCard>
  );
};

export default BotStatusCard;
